import React, { Fragment, useRef, useState } from 'react';
import { Box, Typography, IconButton, FormControlLabel, Switch, Divider, Slider, Tooltip } from '@mui/material';
import { Page, updatePage } from 'features/document/documentSlice';
import { useDeleteFileMutation, useGetFileQuery, usePostFileMutation } from 'features/files/filesApi';
import { toBase64 } from 'utils/fileToBase64';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from 'app/hooks';
import ModeEditRoundedIcon from '@mui/icons-material/ModeEditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AlignmentRadioGroup from 'components/AlignmentRadioGroup/AlignmentRadioGroup';
import { Tag } from 'components/RadioTag/RadioTag';
import Button from 'components/Button/Button';
import { HiddenButton } from '../components/SidebarElementFactory/components/HeaderSidebar/HeaderSidebar.styled';
import FileUploadIcon from '@mui/icons-material/FileUploadRounded';
import StyleCheckbox from 'components/StyleCheckbox/StyleCheckbox';

interface PageSettingsSidebarProps {
  page: Page;
}

export default function PageSettingsSidebar({ page }: PageSettingsSidebarProps) {
  const dispatch = useAppDispatch();
  const {
    alignment,
    backgroundColor,
    backgroundImageId,
    overflowBackgroundImageId,
    showBackgroundColor,
    showBackgroundImage,
    showOverflowBackgroundImage,
    backgroundImageOpacity,
    addSheetPadding,
    hideHeaderAndFooterOnOverflow,
    isLegal,
  } = page;
  const hiddenBackgroundImageInput = useRef<HTMLInputElement>(null);
  const hiddenOverflowBackgroundImageInput = useRef<HTMLInputElement>(null);
  const { id } = useParams();
  const [postFile] = usePostFileMutation();
  const [deleteFile] = useDeleteFileMutation();
  const [backgroundImageAlignment, setBackgroundImageAlignment] = useState<Tag>({
    label: alignment || 'center',
    value: alignment || 'center',
  });

  const { data: backgroundImageData, isLoading: isLoadingBackgroundImage } = useGetFileQuery(
    Number(backgroundImageId),
    {
      skip: !backgroundImageId,
    }
  );

  const { data: overflowBackgroundImageData, isLoading: isLoadingOverflowBackgroundImage } = useGetFileQuery(
    Number(overflowBackgroundImageId),
    {
      skip: !overflowBackgroundImageId,
    }
  );

  const handleOnChangeIsLegal = () => {
    dispatch(
      updatePage({
        property: 'isLegal',
        value: !isLegal,
      })
    );
  }

  const handleOnChangeShowBackgroundColor = () => {
    dispatch(
      updatePage({
        property: 'showBackgroundColor',
        value: !showBackgroundColor,
      })
    );
  };

  const handleOnChangeShowBackgroundImage = () => {
    dispatch(
      updatePage({
        property: 'showBackgroundImage',
        value: !showBackgroundImage,
      })
    );
  };

  const handleOnChangeShowOverflowBackgroundImage = () => {
    dispatch(
      updatePage({
        property: 'showOverflowBackgroundImage',
        value: !showOverflowBackgroundImage,
      })
    );
  };

  const handleOnChangeHideOnOverflow = () => {
    dispatch(
      updatePage({
        property: 'hideHeaderAndFooterOnOverflow',
        value: !hideHeaderAndFooterOnOverflow,
      })
    );
  };

  const handlePadding = () => {
    dispatch(
      updatePage({
        property: 'addSheetPadding',
        value: !addSheetPadding,
      })
    );
  };

  const handleOnClickBackgroundImage = () => {
    hiddenBackgroundImageInput.current?.click();
  };

  const handleOnClickOverflowBackgroundImage = () => {
    hiddenOverflowBackgroundImageInput.current?.click();
  };

  const handleUploadBackgroundImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files !== null) {
      const srcName = event.currentTarget.files[0].name;
      const imageBase64 = await toBase64(event.currentTarget.files[0]);
      const imageContent = imageBase64.split(',')[1];
      const fileResult = await postFile({
        type: 'jpeg',
        fileName: srcName,
        fileContent: imageContent,
      }).unwrap();
      if (backgroundImageId && fileResult && !id) {
        deleteFile(Number(backgroundImageId));
      }
      dispatch(
        updatePage({
          property: 'backgroundImageId',
          value: fileResult.data?.id,
        })
      );
    }
    event.target.value = '';
  };

  const handleUploadOverflowBackgroundImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files !== null) {
      const srcName = event.currentTarget.files[0].name;
      const imageBase64 = await toBase64(event.currentTarget.files[0]);
      const imageContent = imageBase64.split(',')[1];
      const fileResult = await postFile({
        type: 'jpeg',
        fileName: srcName,
        fileContent: imageContent,
      }).unwrap();
      if (overflowBackgroundImageId && fileResult && !id) {
        deleteFile(Number(overflowBackgroundImageId));
      }
      dispatch(
        updatePage({
          property: 'overflowBackgroundImageId',
          value: fileResult.data?.id,
        })
      );
    }
    event.target.value = '';
  };

  const handleOnDeleteBackgroundImage = async () => {
    if (!id) {
      dispatch(
        updatePage({
          property: 'backgroundImageId',
          value: undefined,
        })
      );
      await deleteFile(Number(backgroundImageId));
    } else {
      dispatch(
        updatePage({
          property: 'backgroundImageId',
          value: undefined,
        })
      );
    }
  };

  const handleOnDeleteOverflowBackgroundImage = async () => {
    if (!id) {
      dispatch(
        updatePage({
          property: 'overflowBackgroundImageId',
          value: undefined,
        })
      );
      await deleteFile(Number(overflowBackgroundImageId));
    } else {
      dispatch(
        updatePage({
          property: 'overflowBackgroundImageId',
          value: undefined,
        })
      );
    }
  };

  const handleOnChangeAlignment = (newAlignment: Tag) => {
    setBackgroundImageAlignment(newAlignment);

    dispatch(
      updatePage({
        property: 'alignment',
        value: newAlignment.value,
      })
    );
  };

  const handleOnChangeOpacity = (_event: Event | React.SyntheticEvent<Element, Event>, val: number | number[]) => {
    if (typeof val === 'number') {
      dispatch(
        updatePage({
          property: 'backgroundImageOpacity',
          value: val,
        })
      );
    }
  };

  const onChangeBackgroundColor = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      updatePage({
        property: 'backgroundColor',
        value: event.target.value,
      })
    );
  };

  const getOpacityLabel = (opacity: number) => {
    const percentageString: string = `${Math.round(opacity * 100)}%`;
    return percentageString;
  };

  return (
    <Box width={'100%'}>
      <Box alignItems={'center'} display={'flex'} justifyContent={'space-between'}>
        <Typography fontSize={'16px'} fontWeight={500} color='#474747'>
          Page Settings
        </Typography>
      </Box>
      <Box display={'flex'} flexDirection={'column'} rowGap={'8px'} marginTop={'32px'}>
      <Box display={'flex'} flexDirection={'column'} rowGap={'24px'}>
      <FormControlLabel
            control={
              <Switch
                checked={isLegal || false}
                color='secondary'
                onChange={handleOnChangeIsLegal}
              />
            }
            label='Legal Size'
          />  
</Box>
        <Box display={'flex'} flexDirection={'column'} rowGap={'24px'}>
          
          <FormControlLabel
            control={
              <Switch
                checked={showBackgroundColor || false}
                color='secondary'
                onChange={handleOnChangeShowBackgroundColor}
              />
            }
            label='Background'
          />
          {showBackgroundColor && (
            <Fragment>
              <input type='color' value={backgroundColor} onChange={event => onChangeBackgroundColor(event)} />
              <Divider sx={{ color: '#C9CACB', width: '100%', marginBottom: '16px' }} />
            </Fragment>
          )}
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={showBackgroundImage || false}
              color='secondary'
              onChange={handleOnChangeShowBackgroundImage}
            />
          }
          label='Background Image'
        />
        {showBackgroundImage && (
          <Box display={'flex'} marginTop={'16px'}>
            {!backgroundImageId || isLoadingBackgroundImage ? (
              <HiddenButton onClick={handleOnClickBackgroundImage}>
                <Button disabled={isLoadingBackgroundImage} sx={{ width: '100%' }} variant='secondary'>
                  Upload Image
                  <FileUploadIcon />
                </Button>
              </HiddenButton>
            ) : (
              <Box>
                <Box display={'flex'} alignItems={'flex-start'} gap={'16px'} width={'100%'}>
                  <img alt='uploaded' src={backgroundImageData?.data.url} width={'226px'} />
                  <Box display={'flex'} flexDirection={'column'} rowGap={'16px'}>
                    <Tooltip title='Edit' arrow>
                      <IconButton aria-label='replace image' onClick={handleOnClickBackgroundImage}>
                        <ModeEditRoundedIcon color='primary' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete' arrow>
                      <IconButton aria-label='delete' onClick={handleOnDeleteBackgroundImage}>
                        <DeleteRoundedIcon color='primary' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box
                  display={'flex'}
                  alignItems={'flex-start'}
                  justifyContent={'space-between'}
                  width={'100%'}
                  marginTop={'24px'}
                  marginBottom={'8px'}
                >
                  <Typography fontSize={'14px'} lineHeight={'24px'} color={'#474747'}>
                    Opacity
                  </Typography>
                  <Typography fontSize={'16px'} fontWeight={'600'} lineHeight={'16px'} color={'#000000'}>
                    {getOpacityLabel(Number(backgroundImageOpacity || 1))}
                  </Typography>
                </Box>
                <Slider
                  size='small'
                  min={0.1}
                  value={backgroundImageOpacity || 1}
                  step={0.1}
                  max={1}
                  aria-label='Small'
                  valueLabelDisplay='off'
                  onChange={handleOnChangeOpacity}
                  sx={{ '.MuiSlider-thumb': { boxShadow: 'none' }, width: '98%' }}
                />
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  alignItems={'flex-start'}
                  width={'100%'}
                  marginTop={'24px'}
                  marginBottom={'8px'}
                >
                  <Typography fontSize={'14px'} lineHeight={'24px'} color={'#474747'} marginBottom={'16px'}>
                    Alignment
                  </Typography>
                  <AlignmentRadioGroup selected={backgroundImageAlignment} handleChange={handleOnChangeAlignment} />
                </Box>
              </Box>
            )}
            <input type='file' onChange={handleUploadBackgroundImage} ref={hiddenBackgroundImageInput} hidden />
          </Box>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={hideHeaderAndFooterOnOverflow || false}
              color='secondary'
              onChange={handleOnChangeHideOnOverflow}
            />
          }
          label='Hide Header & Footer On Overflow'
        />
        {hideHeaderAndFooterOnOverflow && (
          <Fragment>
            <FormControlLabel
              control={
                <Switch
                  checked={showOverflowBackgroundImage || false}
                  color='secondary'
                  onChange={handleOnChangeShowOverflowBackgroundImage}
                />
              }
              label='Overflow Background Image'
            />
            {showOverflowBackgroundImage && (
              <Box display={'flex'} marginTop={'16px'}>
                {!overflowBackgroundImageId || isLoadingOverflowBackgroundImage ? (
                  <HiddenButton onClick={handleOnClickOverflowBackgroundImage}>
                    <Button disabled={isLoadingOverflowBackgroundImage} sx={{ width: '100%' }} variant='secondary'>
                      Upload Image
                      <FileUploadIcon />
                    </Button>
                  </HiddenButton>
                ) : (
                  <Box>
                    <Box display={'flex'} alignItems={'flex-start'} gap={'16px'} width={'100%'}>
                      <img alt='uploaded' src={overflowBackgroundImageData?.data.url} width={'226px'} />
                      <Box display={'flex'} flexDirection={'column'} rowGap={'16px'}>
                        <Tooltip title='Edit' arrow>
                          <IconButton aria-label='replace image' onClick={handleOnClickOverflowBackgroundImage}>
                            <ModeEditRoundedIcon color='primary' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete' arrow>
                          <IconButton aria-label='delete' onClick={handleOnDeleteOverflowBackgroundImage}>
                            <DeleteRoundedIcon color='primary' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                )}
                <input
                  type='file'
                  onChange={handleUploadOverflowBackgroundImage}
                  ref={hiddenOverflowBackgroundImageInput}
                  hidden
                />
              </Box>
            )}
          </Fragment>
        )}
        <StyleCheckbox
          option={{ label: 'Add Sheet Padding', value: '50px' }}
          handleChange={() => handlePadding()}
          checked={addSheetPadding}
        />
      </Box>
    </Box>
  );
}
